import React, { useCallback, useMemo } from "react";
import { DYNAMIC_ISLAND } from "../../../../config/dynamicIsland.configuration";
import { useReducedMotion } from "../../../../hooks/useReducedMotion.hook";
import ItineraryDetailsSummary from "../../../ItinerariesPlanner/itineraryDetails/ItineraryDetailsSummary";
import { type DynamicIslandVariantContentProps } from "../../dynamicIsland.registry";
import { type DynamicIslandItinerarySummaryPayload } from "../../dynamicIsland.types";
import DynamicIslandItinerarySummaryMotionLegItem from "./DynamicIslandItinerarySummaryMotionLegItem";
import { handleItinerarySummaryLegClick, resolveItinerarySummarySelectedLeg } from "./itinerarySummary.interactions.tools";

/**
 * Dynamic Island content for a selected multi-leg itinerary.
 * Generic background and horizontal scroll are enabled via variant `shell` registration.
 *
 * @param props Variant content props.
 * @returns Itinerary summary leg progression row.
 */
const DynamicIslandItinerarySummaryContent: React.FC<
    DynamicIslandVariantContentProps<DynamicIslandItinerarySummaryPayload>
> = ( { payload, isDarkUi, hostContext } ) => {
    const reducedMotion = useReducedMotion();
    const selectedLeg = useMemo( () => resolveItinerarySummarySelectedLeg( payload ), [ payload ] );
    const animationsEnabled = DYNAMIC_ISLAND.animations.enabled && !reducedMotion;

    const handleLegClick = useCallback( ( leg: Parameters<typeof handleItinerarySummaryLegClick>[0] ): void => {
        handleItinerarySummaryLegClick( leg, hostContext );
    }, [ hostContext ] );

    const wrapLegItem = useCallback(
        (
            node: React.ReactNode,
            context: {
                "leg":        Parameters<typeof handleItinerarySummaryLegClick>[0];
                "index":      number;
                "isSelected": boolean;
            },
        ): React.ReactNode => {
            if ( !animationsEnabled ) {
                return node;
            }

            return (
                <DynamicIslandItinerarySummaryMotionLegItem
                    index={context.index}
                    isSelected={context.isSelected}
                    reducedMotion={reducedMotion}
                >
                    {node}
                </DynamicIslandItinerarySummaryMotionLegItem>
            );
        },
        [ animationsEnabled, reducedMotion ],
    );

    return (
        <ItineraryDetailsSummary
            isDarkUi={isDarkUi}
            layout="island"
            legs={payload.legs}
            onLegClick={handleLegClick}
            selectedLeg={selectedLeg}
            wrapLegItem={animationsEnabled ? wrapLegItem : undefined}
        />
    );
};

export default DynamicIslandItinerarySummaryContent;
